import {
	useCallback,
	useDebugValue,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
// @ts-ignore
import { useLocation as actualUseLocation } from "react-router-dom";

import * as NavigationUtils from "../util/NavigationUtils";
import { getHash, Route } from "../util/NavigationUtils";
import { BrightmileRouteOptions } from "../../Routing";
import { useSelector } from "./ReduxHooks";
import { StoreState } from "../../store/RootReducer";
import * as NotificationUtils from "../util/NotificationUtils";
import * as UserSelectors from "../../store/User/User.selector";

export const useRouteCheck = (route: Route): boolean => {
	const currentRoute = useRoute();
	return currentRoute?.id === route.id;
};

export const useRoute = (): Route<BrightmileRouteOptions> | null => {
	const location = useLocation();

	return useMemo(
		() => NavigationUtils.getRouteForPath(location.pathname),
		[location.pathname],
	);
};

export const useRedirect = () => NavigationUtils.go;

export const useLocation: () => Location = actualUseLocation as any;

export const useHeaderVisibility: () => boolean = () => {
	const route = useRoute();
	const { loggedInUser } = useSelector((state: StoreState) => ({
		loggedInUser: UserSelectors.selectLoggedInUser(state),
	}));

	const visible = Boolean(
		route?.options?.navigation !== false && loggedInUser?.uid,
	);
	useDebugValue(visible);
	return visible;
};

export const useScrollTopVisibility: () => boolean = () => {
	const route = useRoute();
	return route?.options?.navigation !== false;
};

export const useIsRouteAccessible: () => boolean = () => {
	const route = useRoute();
	const { hasTrends } = useSelector((state: StoreState) => ({
		hasTrends: UserSelectors.selectCurrentScopeHasTrends(state),
	}));

	return Boolean(
		(hasTrends ?? true) || !(route?.options?.blockNoTrends ?? false),
	);
};

export const useNotificationVisible: (
	type: "splash" | "banner",
	level?: number,
) => boolean = (type, level) => {
	const route = useRoute();

	if (!level) {
		return false;
	}

	const visibility = NotificationUtils.notificationActive(level);

	if (type === NotificationUtils.NOTIFICATION_TYPE_SPLASH) {
		return route?.options?.splashNotification !== false && visibility.splash;
	} else if (type === NotificationUtils.NOTIFICATION_TYPE_BANNER) {
		return route?.options?.notification !== false && visibility.banner;
	}

	return false;
};

export const useQueryParam = <T = string>(
	key: string,
): [T, (update: T | undefined) => void] => {
	const [queryParams, setQueryParams] = useQueryParams([key]);
	const setQueryParam = useCallback(
		(value: T | undefined, push?: boolean) => {
			setQueryParams({ [key]: value as any }, push);
		},
		[key, setQueryParams],
	);
	return [queryParams[key] as any, setQueryParam];
};

export const useQueryParams = <Q extends Record<string, any>>(
	keys: (keyof Q)[],
): [Q, (update: Partial<Q>, push?: boolean) => void] => {
	const initialKeys = useRef(keys);
	const [queryParams, setQueryParams] = useState<Q>(
		keys.reduce((queryParams, key) => {
			queryParams[key] = NavigationUtils.getQueryParam(key as string);
			return queryParams;
		}, {} as any),
	);

	const updateIfNeeded = useCallback(() => {
		let updated = null;
		for (const key of initialKeys.current) {
			const value = NavigationUtils.getQueryParam(key as string);
			if (queryParams[key] !== value) {
				if (!updated) {
					updated = { ...queryParams };
				}
				updated[key] = value;
			}
		}
		if (updated) {
			setQueryParams(updated);
		}
	}, [queryParams]);

	useEffect(() => {
		const listenerId = NavigationUtils.onHistoryChange(updateIfNeeded);

		return () => {
			NavigationUtils.clearHistoryChange(listenerId);
		};
	}, [updateIfNeeded]);

	return [queryParams, NavigationUtils.setQueryParams];
};

export const usePreload = (href: string | undefined) => {
	const route = useMemo(
		() => href && NavigationUtils.getRouteForPath(href),
		[href],
	);
	useEffect(() => {
		if (route) {
			NavigationUtils.preload(route);
		}
	}, [route]);
};

export const useHash = (): [string | null, (hash: string | null) => void] => {
	const [hash, setHash] = useState(getHash());

	const updateHash = useCallback((hash: string | null) => {
		NavigationUtils.setHash(hash);
		setHash(hash);
	}, []);

	useEffect(() => {
		const listenerId = NavigationUtils.onHashChange("*", (hash) => {
			setHash(hash);
		});
		return () => NavigationUtils.clearHashChange(listenerId);
	});

	return [hash, updateHash];
};
